import { bauValidatePassword } from './bauPasswordValidations';
import {
  BLANK_SPACE_REG_EX,
  LETTER_REG_EX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PRINTABLE_ASCII_OR_SPACE_REG_EX,
} from './passwordConstants';

type ValidatePasswordOptions = Parameters<typeof bauValidatePassword>[1];

type PasswordValidationResult = ReturnType<typeof bauValidatePassword> & {
  hasBlankSpace?: boolean;
  missingLetter?: boolean;
};

export type { PasswordValidationResult };

/**
 * CPCS differs from BAU in max length (64), printable-ASCII (no spaces),
 * allowing BAU-restricted symbols, and requiring at least one letter. Only the
 * sequence, repetition and infoSec checks are reused from the BAU validator;
 * everything else is computed here so a library change can't silently alter the
 * CPCS ruleset.
 */
const cpcsValidatePassword = (
  value: string,
  options: ValidatePasswordOptions = {},
): PasswordValidationResult => {
  const {
    hasFourCharRepeated,
    hasFourCharSequence,
    infoSecViolation,
    missingNumberOrSymbol,
  } = bauValidatePassword(value, options);

  const length = value.length;

  const result = {
    // A space is a printable-ASCII character, so it's carved out into its own
    // check (below) and given its own hint instead of being folded into
    // `hasNonValidChars`, which now only covers non-space unsupported chars.
    hasBlankSpace: BLANK_SPACE_REG_EX.test(value),
    // Printable-ASCII already rejects double-byte input, so flagging both would
    // render the same hint copy twice.
    hasDoubleByte: false,
    hasFourCharRepeated,
    hasFourCharSequence,
    hasNonValidChars:
      length > 0 && !PRINTABLE_ASCII_OR_SPACE_REG_EX.test(value),
    hasNonValidSymbol: false,
    infoSecViolation,
    missingLetter: !LETTER_REG_EX.test(value),
    missingNumberOrSymbol,
    tooLong: length > PASSWORD_MAX_LENGTH,
    tooShort: length < PASSWORD_MIN_LENGTH,
    valueMissing: !length,
  };

  const errors = Object.keys(result).filter(
    key => result[key as keyof typeof result],
  );

  return { ...result, errors, isValid: !errors.length };
};

export { cpcsValidatePassword };
